<template>
  <div class="cuit-input" :class="{ 'cuit-input--ok': state === 'ok', 'cuit-input--bad': state === 'bad' }">
    <input
      ref="inp"
      class="fi cuit-input__field"
      type="text"
      inputmode="numeric"
      maxlength="13"
      :value="display"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      @input="onInput"
      @blur="$emit('blur', $event)"
    />
    <button
      v-if="showArca"
      type="button"
      class="cuit-input__arca"
      :disabled="state !== 'ok' || busy"
      @click="onArca"
      :title="arcaTitle"
    >
      <svg v-if="!busy" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/>
      </svg>
      <span v-else class="cuit-input__spin"></span>
      {{ busy ? 'Consultando...' : 'Traer de ARCA' }}
    </button>
    <span v-if="state === 'ok'" class="cuit-input__hint cuit-input__hint--ok">
      ✓ CUIT válido <span v-if="tipoTxt">· {{ tipoTxt }}</span>
    </span>
    <span v-if="state === 'bad'" class="cuit-input__hint cuit-input__hint--bad">
      CUIT inválido (DV o prefijo)
    </span>
  </div>
</template>

<script>
import api from '../services/api';
import { formatPartial, isValid, normalize, tipo } from '../utils/cuit';

export default {
  name: 'CuitInput',
  props: {
    modelValue: { type: String, default: '' },
    placeholder: { type: String, default: '30-12345678-9' },
    autocomplete: { type: String, default: 'off' },
    showArca: { type: Boolean, default: true }
  },
  emits: ['update:modelValue', 'blur', 'arca-data'],
  data() { return { busy: false, arcaError: '' }; },
  computed: {
    display() { return formatPartial(this.modelValue); },
    state() {
      const c = normalize(this.modelValue);
      if (!c.length) return 'empty';
      if (c.length < 11) return 'typing';
      return isValid(c) ? 'ok' : 'bad';
    },
    tipoTxt() {
      const t = tipo(this.modelValue);
      return t === 'empresa' ? 'Empresa' : t === 'persona' ? 'Persona' : '';
    },
    arcaTitle() {
      if (this.state !== 'ok') return 'Completá un CUIT válido para consultar ARCA';
      return this.arcaError || 'Traer datos desde el padrón AFIP';
    }
  },
  methods: {
    onInput(e) {
      const normalized = normalize(e.target.value);
      this.$emit('update:modelValue', normalized);
    },
    async onArca() {
      this.busy = true; this.arcaError = '';
      try {
        const { data } = await api.get(`/arca/padron/${normalize(this.modelValue)}`);
        this.$emit('arca-data', data);
      } catch (e) {
        const st = e?.response?.status;
        const msg = e?.response?.data?.message || 'Error consultando ARCA';
        this.arcaError = st === 503 ? 'ARCA no configurado en el backend' : msg;
        this.$emit('arca-data', { error: this.arcaError, status: st });
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style scoped>
.cuit-input{ display:flex; flex-wrap:wrap; gap:8px; align-items:center; }
.cuit-input__field{ flex:1; min-width:180px; font-family:'SF Mono','Consolas',monospace; letter-spacing:.02em; }
.cuit-input--ok  .cuit-input__field{ border-color:#2E9E67; box-shadow:0 0 0 3px rgba(46,158,103,0.15); }
.cuit-input--bad .cuit-input__field{ border-color:#c24545; box-shadow:0 0 0 3px rgba(194,69,69,0.15); }

.cuit-input__arca{
  display:inline-flex; align-items:center; gap:6px;
  padding:8px 12px; border:1px solid var(--border-s); border-radius:8px;
  background:var(--acc-xlt); color:var(--accent);
  font-size:12px; font-weight:600; cursor:pointer; font-family:inherit;
  transition:background .15s, border-color .15s;
  white-space:nowrap;
}
.cuit-input__arca:hover:not(:disabled){ background:var(--acc-lt); border-color:var(--accent); }
.cuit-input__arca:disabled{ opacity:.45; cursor:not-allowed; }

.cuit-input__hint{ width:100%; font-size:11px; margin-top:-2px; }
.cuit-input__hint--ok{ color:#2E9E67; }
.cuit-input__hint--bad{ color:#c24545; }

.cuit-input__spin{
  width:12px; height:12px; border-radius:50%;
  border:2px solid rgba(21,96,189,.2); border-top-color:var(--accent);
  animation:cuit-spin .6s linear infinite;
}
@keyframes cuit-spin{ to{ transform:rotate(360deg); } }
</style>
